import { retryUnlessRateLimited } from '../utils/queryRetry'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import TwilioBalanceService, { type TwilioBalanceStatus } from '../services/twilioBalance'

export const TWILIO_BALANCE_QUERY_KEY = ['twilio-balance'] as const

/** Période de rafraîchissement du bandeau : 5 min (le serveur relève toutes les 15). */
export const TWILIO_BALANCE_REFETCH_MS = 5 * 60 * 1000

/**
 * LOT #142 / E-20 — état du solde Twilio (GET /api/twilio-balance).
 *
 * Rafraîchi toutes les 5 min ET au retour de visibilité de l'onglet
 * (`refetchOnWindowFocus` — React Query écoute `visibilitychange`) : un iPad
 * qui se réveille doit savoir tout de suite si la téléphonie est tombée
 * pendant son sommeil. `staleTime` court pour que ce retour déclenche bien un
 * appel plutôt que de servir le cache.
 *
 * `enabled` : rien n'est interrogé tant que l'utilisateur n'est pas connu.
 */
export function useTwilioBalance(enabled: boolean = true) {
  return useQuery({
    queryKey: TWILIO_BALANCE_QUERY_KEY,
    queryFn: () => TwilioBalanceService.getStatus(),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: TWILIO_BALANCE_REFETCH_MS,
    refetchOnWindowFocus: true,
    retry: retryUnlessRateLimited, // une tentative de plus, jamais sur un 429 (#194, D-G)
  })
}

/**
 * Relevé forcé (super-admin, bouton « Actualiser » de la carte). Le snapshot
 * rendu remplace directement le cache : le bandeau et la carte se mettent à
 * jour sans second aller-retour. Un 429 (throttle 1/min) est laissé à
 * l'appelant (`isRefreshThrottledError`).
 */
export function useRefreshTwilioBalance() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => TwilioBalanceService.refresh(),
    onSuccess: (status: TwilioBalanceStatus) => {
      queryClient.setQueryData(TWILIO_BALANCE_QUERY_KEY, status)
    },
  })
}
